import { apiClient, type PaginatedResponse } from '../../../api/client'
import { buildPaginationParams, type PaginationOptions } from '../../../api/pagination'
import { ChatchannelApi } from '../../../generated-types/apis/ChatchannelApi'
import { ChatmessageApi } from '../../../generated-types/apis/ChatmessageApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { createChannel, createMessage } from '../utils/functions'
import { type Channel, type Message } from '../utils/types'

const log = new LogTool({ context: 'ChatAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 * This function is called each time to ensure the token is fresh
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      log.debug('Getting access token from cookies:', token ? 'Token present' : 'No token')
      return token || ''
    }
  })
}

/**
 * Get or create API instances with fresh configuration
 */
function getChannelsApi(): ChatchannelApi {
  return new ChatchannelApi(getApiConfiguration())
}

function getMessagesApi(): ChatmessageApi {
  return new ChatmessageApi(getApiConfiguration())
}

/**
 * Fetch paginated channels from API
 * Note: Uses apiClient to support expand parameters
 */
export async function fetchChannels(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Channel>> {
  log.info('Fetching channels with options:', options)

  const params = buildPaginationParams(options)
  const response = await apiClient.get<PaginatedResponse<any>>(
    `v1/chat/channel/?${params.join('&')}`
  )

  const channels = response.results ? response.results.map(createChannel) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: channels,
  }
}

/**
 * Fetch a single channel by ID
 * Note: Uses apiClient for expand parameters
 */
export async function fetchChannel(
  channelId: string | number,
  expand?: string[]
): Promise<Channel> {
  log.info('Fetching channel:', channelId)

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 
    ? `v1/chat/channel/${channelId}/?${params.join('&')}` 
    : `v1/chat/channel/${channelId}/`

  const response = await apiClient.get<any>(fetchUrl)
  return createChannel(response)
}

/**
 * Fetch messages for a channel
 * Note: Uses apiClient for nested resource endpoint
 */
export async function fetchMessages(
  channelId: string | number,
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Message>> {
  log.info('Fetching messages for channel:', channelId)

  const params = buildPaginationParams(options)
  const response = await apiClient.get<PaginatedResponse<any>>(
    `v1/chat/channel/${channelId}/messages/?${params.join('&')}`
  )

  const messages = response.results.map((m: any) => createMessage(m))

  return {
    ...response,
    results: messages,
  }
}

/**
 * Fetch unread messages count
 * Note: Uses apiClient for custom aggregation endpoint
 */
export async function fetchUnreadMessagesCountApi(): Promise<number> {
  log.info('Fetching unread messages count')

  const response = await apiClient.get<{ count: number }>('v1/chat/channel/unread_messages_count/')
  return response.count || 0
}

/**
 * Create a new channel
 */
export async function createChannelApi(channelData: any): Promise<Channel> {
  log.info('Creating channel:', channelData)

  const response = await getChannelsApi().v1ChatChannelCreate({
    aPIChannelCreationRequest: channelData,
  })
  return createChannel(response)
}

/**
 * Update an existing channel
 */
export async function updateChannelApi(
  channelId: string | number,
  channelData: any
): Promise<Channel> {
  log.info('Updating channel:', channelId)

  const response = await getChannelsApi().v1ChatChannelUpdate({
    id: String(channelId),
    aPIChannelCreationRequest: channelData,
  })
  return createChannel(response)
}

/**
 * Patch an existing channel (partial update)
 */
export async function patchChannelApi(
  channelId: string | number,
  channelData: Partial<any>
): Promise<Channel> {
  log.info('Patching channel:', channelId)

  const response = await getChannelsApi().v1ChatChannelPartialUpdate({
    id: String(channelId),
    aPIPatchedChannelRequest: channelData,
  })
  return createChannel(response)
}

/**
 * Delete a channel
 */
export async function deleteChannelApi(channelId: string | number): Promise<void> {
  log.info('Deleting channel:', channelId)

  await getChannelsApi().v1ChatChannelDestroy({ id: String(channelId) })
}

/**
 * Create a new message
 * Note: Uses apiClient - generated API requires channelPk which this function doesn't accept
 */
export async function createMessageApi(messageData: any): Promise<Message> {
  log.info('Creating message:', messageData)

  const response = await apiClient.post<any>('v1/chat/message/', messageData)
  return createMessage(response)
}

/**
 * Post a message to a channel
 */
export async function postMessageApi(
  channelId: string | number,
  messageData: any
): Promise<Message> {
  log.info('Posting message to channel:', channelId)

  const response = await getMessagesApi().v1ChatChannelMessagesCreate({
    channelPk: String(channelId),
    aPIMessageRequest: messageData,
  })
  return createMessage(response)
}

/**
 * Update an existing message
 * Note: Uses apiClient - generated API requires channelPk which this function doesn't accept
 */
export async function updateMessageApi(
  messageId: string | number,
  messageData: any
): Promise<Message> {
  log.info('Updating message:', messageId)

  const response = await apiClient.put<any>(`v1/chat/message/${messageId}/`, messageData)
  return createMessage(response)
}

/**
 * Patch an existing message (partial update)
 * Note: Uses apiClient - generated API requires channelPk which this function doesn't accept
 */
export async function patchMessageApi(
  messageId: string | number,
  messageData: Partial<any>
): Promise<Message> {
  log.info('Patching message:', messageId)

  const response = await apiClient.patch<any>(`v1/chat/message/${messageId}/`, messageData)
  return createMessage(response)
}

/**
 * Delete a message (soft delete with custom deleted text)
 * Note: Uses apiClient - this is a soft delete that patches the message body
 */
export async function deleteMessageApi(
  messageSelf: string,
  deletedText: string
): Promise<Message> {
  log.info('Deleting message:', messageSelf)

  const response = await apiClient.patch<any>(`v1/${messageSelf}`, { body: deletedText })
  return createMessage(response)
}

/**
 * Mark channel as read
 * Note: Uses apiClient for custom action endpoint
 */
export async function markChannelAsReadApi(channelId: string | number): Promise<void> {
  log.info('Marking channel as read:', channelId)

  await apiClient.post<any>(`v1/chat/channel/${channelId}/mark_as_read/`, {})
}

/**
 * Update channel interacted timestamp
 * Note: Uses apiClient for custom action endpoint (PATCH method as per backend)
 */
export async function updateChannelInteractedApi(channelId: string | number): Promise<void> {
  log.info('Updating channel interacted timestamp:', channelId)

  await apiClient.patch<any>(`v1/chat/channel/${channelId}/interacted/`, {})
}

/**
 * Add a member to a channel
 * Note: Uses apiClient for custom nested endpoint
 */
export async function addMemberToChannelApi(
  membersUrl: string,
  userSelf: string
): Promise<void> {
  log.info('Adding member to channel:', membersUrl, userSelf)

  await apiClient.post<any>(`v1/${membersUrl}`, { user: userSelf })
}

/**
 * Leave a channel
 * Note: Uses apiClient for custom action endpoint
 */
export async function leaveChannelApi(channelId: string | number): Promise<void> {
  log.info('Leaving channel:', channelId)

  await apiClient.post<any>(`v1/chat/channel/${channelId}/leave/`, {})
}
